<template>
  <div
    :class="sizeClass + ' relative mx-auto h-full flex flex-col'"
    :style="ratio ? {aspectRatio: ratio} : {}"
  >
    <template v-if="pageCount > 1">
      <button
        v-if="page > 1"
        class="absolute left-2 top-1/2 -translate-y-1/2 z-10 size-9 rounded-full flex items-center justify-center bg-primary text-white shadow-custom hover:brightness-110 disabled:cursor-wait transition"
        :disabled="loading"
        type="button"
        title="Page précédente"
        aria-label="Page précédente"
        @click.stop="show(page - 1)"
      >
        <ChevronLeft class="size-5" />
      </button>
      <button
        v-if="page < pageCount"
        class="absolute right-2 top-1/2 -translate-y-1/2 z-10 size-9 rounded-full flex items-center justify-center bg-primary text-white shadow-custom hover:brightness-110 disabled:cursor-wait transition"
        :disabled="loading"
        type="button"
        title="Page suivante"
        aria-label="Page suivante"
        @click.stop="show(page + 1)"
      >
        <ChevronRight class="size-5" />
      </button>
      <div
        class="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center rounded-full bg-white px-1.5 py-0.5 shadow-custom"
        @click.stop
      >
        <button
          v-for="number in pageCount"
          :key="number"
          class="size-6 flex items-center justify-center disabled:cursor-wait"
          :disabled="loading"
          type="button"
          :title="`Page ${number}/${pageCount}`"
          :aria-label="`Page ${number}`"
          :aria-current="number === page ? 'page' : undefined"
          @click="show(number)"
        >
          <span
            :class="'size-2.5 rounded-full transition-colors ' + (number === page ? 'bg-primary' : 'bg-[#cecece]')"
          />
        </button>
      </div>
    </template>
    <div
      v-if="loading"
      :class="sizeClass + ' flex-1 flex items-center justify-center'"
    >
      <BaseSpinner v-if="loaderType === 'spinner'" :size="spinnerSize" />
      <div v-else class="w-full h-full bg-gray-200 animate-pulse rounded">
        <FileImage class="w-full h-full text-gray-500 stroke-1" />
      </div>
    </div>
    <div
      v-else
      class="grid justify-items-center"
    >
      <div
        v-if="pages[page]"
        class="relative"
      >
        <img
          :src="pages[page]"
          :alt="`ordonnance, page ${page}`"
          :class="sizeClass + ' cursor-pointer object-contain'"
          @click="!preventClick && print()"
        >
        <div
          v-if="printButton"
          class="absolute bottom-8 right-8"
        >
          <BaseButton
            primary
            class="rounded-3xl"
            style="padding:10px !important"
            @click="print()"
          >
            <v-icon color="white">
              mdi-printer
            </v-icon>
          </BaseButton>
        </div>
      </div>
      <Prescription
        v-else
        class="max-h-28 w-full"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import FileUrl from "@/core/interfaces/FileUrl";
import Prescription from "@/core/components/icons/Prescription.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";
import SecurePrescriptionPageService from "@/core/services/SecurePrescriptionPageService";
import {ChevronLeft, ChevronRight, FileImage} from "@lucide/vue";
import fileHelper from "@/core/helpers/fileHelper";

/**
 * A prescription read page by page, on its large thumbnail.
 *
 * The HDS renders the large thumbnail of one page at a time and tells, with
 * the first one, how many pages the prescription has. The other pages are
 * fetched only when the pharmacist turns to them, never ahead: every image
 * costs two requests against the per IP limit the HDS load balancer applies
 * to the whole pharmacy. Printing goes through the PDF, which holds every
 * page. Against an HDS that does not send the page count, it behaves as a
 * one page prescription.
 */

interface Props {
    urls: FileUrl[];
    size?: number;
    printButton?: boolean;
    ratio?: number;
    preventClick?: boolean;
    loaderType?: 'spinner' | 'skeleton';
}

const {
    urls,
    size = 84,
    ratio = null,
    printButton = false,
    preventClick = false,
    loaderType = 'spinner'
} = defineProps<Props>();

const emit = defineEmits<{
    (e: 'loaded', blob: Blob): void;
    (e: 'error'): void;
}>();

const page = ref(1);
const pageCount = ref(1);
const pages = ref<Record<number, string>>({});
const loading = ref(true);

const sizeClass = computed(() => `w-${size}`);

const spinnerSize = computed(() => size >= 84 ? 'medium' : 'medium-small');

/**
 * Show a page, fetching it the first time only.
 */
const show = (number: number) => {
    const url = urls[0]?.thumbnail.large;

    if (!url) {
        loading.value = false;
        return;
    }

    if (pages.value[number]) {
        page.value = number;
        return;
    }

    loading.value = true;

    SecurePrescriptionPageService.fetch(url, number)
        .then(({blob, pageCount: count}) => {
            pages.value[number] = window.URL.createObjectURL(blob);
            page.value = number;

            if (number === 1) {
                pageCount.value = count;
                emit('loaded', blob);
            }
        })
        .catch(() => {
            if (number === 1) {
                emit('error');
            }
        })
        .finally(() => {
            loading.value = false;
        });
};

/**
 * Print every page through the PDF, or the page shown when there is none.
 */
const print = () => {
    const pdf = urls[0]?.pdf;

    if (pdf) {
        fileHelper.printPdf(pdf);
        return;
    }

    const printWindow = window.open(
        '',
        'modal',
        'width=' + screen.availWidth + ',height=' + screen.availHeight
    );

    if (printWindow) {
        printWindow.document.write('<html lang="fr"><head><title>Visuel</title></head><body>');
        printWindow.document.write('<img src="' + pages.value[page.value] + '" alt="Ordonnance"/>');
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    }
};

/**
 * Release the images fetched so far.
 */
const release = () => {
    Object.values(pages.value).forEach(url => window.URL.revokeObjectURL(url));
    pages.value = {};
};

onMounted(() => show(1));

// On the url itself: some parents build a new array at every render.
watch(() => urls[0]?.thumbnail.large, () => {
    release();
    page.value = 1;
    pageCount.value = 1;
    show(1);
});

onBeforeUnmount(release);
</script>
